import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

interface HistoryContextType {
  previousLocation?: string;
}

const HistoryContext = createContext<HistoryContextType | undefined>(undefined);

interface HistoryProviderProps {
  children: ReactNode;
}

export function HistoryProvider({ children }: HistoryProviderProps) {
  const location = useLocation();
  const prevLocationRef = useRef<string>();
  const [urlSnapshot, setUrlSnapshot] = useState(
    () =>
      window.location.pathname + window.location.search + window.location.hash
  );

  // Get the current URL from window.location (most reliable for nuqs)
  const getCurrentUrl = () =>
    window.location.pathname + window.location.search + window.location.hash;

  // Update URL snapshot when React Router location changes
  useEffect(() => {
    const newUrl = location.pathname + location.search + location.hash;
    setUrlSnapshot(newUrl);
  }, [location]);

  // Listen for nuqs updates using storage events
  // nuqs uses history.pushState/replaceState which triggers this
  useEffect(() => {
    const handleUrlChange = () => {
      const newUrl = getCurrentUrl();
      if (newUrl !== urlSnapshot) {
        setUrlSnapshot(newUrl);
      }
    };

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', handleUrlChange);

    // Listen for pushstate/replacestate events (nuqs uses these)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      handleUrlChange();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      handleUrlChange();
    };

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [urlSnapshot]);

  // Update previous location reference whenever URL changes
  useEffect(() => {
    prevLocationRef.current = urlSnapshot;
  }, [urlSnapshot]);

  const value: HistoryContextType = {
    previousLocation: prevLocationRef.current,
  };

  return (
    <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
  );
}

export function useHistory() {
  const context = useContext(HistoryContext);

  if (context === undefined) {
    throw new Error('useHistory must be used within a HistoryProvider');
  }

  return context;
}
